import { type TSchema, t } from 'elysia'
import { z } from 'zod'

export const GameLimit = t.Object({
  id: t.Number(),
  game_id: t.Number(),
  description: t.String(),
  limit: t.Number(),
  draw_id: t.Number(),
  entity_type_id: t.Number(),
  entity_id: t.Number(),
})

const DrawStatus = t.Union([
  t.Literal('ACTIVE'),
  t.Literal('INACTIVE'),
  t.Literal('SCRUTINIZED'),
  t.Literal('UNKNOWN'),
])

export const Draw = t.Object({
  id: t.Number(),
  identifier: t.String(),
  description: t.String(),
  numbers: t.Optional(t.String()),
  closing_time: t.String(),
  sales: t.Number(),
  prize: t.Number(),
  status: DrawStatus,
})

export const DrawSchedule = t.Object({
  id: t.Number(),
  wk_day_id: t.Number(),
  wk_day: t.String(),
  draw_id: t.Number(),
  closing_time: t.String(),
  schedule: t.String(),
})

export type Draw = (typeof Draw)['static']

export const Agent = t.Object({
  id: t.Number(),
  name: t.String(),
  username: t.String(),
  group_id: t.Number(),
  group_name: t.String(),
  last_login_date: t.MaybeEmpty(t.String()),
  is_active_balance: t.Boolean(),
  balance_amount: t.Number(),
  credit_limit: t.Number(),
  num_terminals: t.Number(),
  status: t.Boolean(),
  providers: t.Array(
    t.Object({
      id: t.Number(),
      description: t.String(),
      identifier: t.String(),
      provider_type_id: t.Number(),
      entity_type_id: t.Number(),
    }),
  ),
})

export type Agent = (typeof Agent)['static']

export const AgentDetail = t.Composite([
  Agent,
  t.Object({
    constraints: t.Array(
      t.Object({
        operational_constraint_id: t.Number(),
        operational_constraint_description: t.String(),
        product_category_id: t.Number(),
        product_category_description: t.String(),

        // generate an id base on `operational_constraint_id` and `product_category_id`
        id: t.String(),
        amount: t.MaybeEmpty(t.Number()),
      }),
    ),
  }),
])

export type AgentDetail = (typeof AgentDetail)['static']

export const EntityTypeSchema = z.enum(['AGENT', 'GROUP', 'ORGANIZATION'])

export const EntityType = t.Enum(EntityTypeSchema.enum)

export type EntityType = (typeof EntityType)['static']

export const getEntityTypeByNumber = (number: number): EntityType => {
  switch (number) {
    case 1:
      return 'AGENT'
    case 10:
      return 'GROUP'
    case 90:
      return 'ORGANIZATION'
    default:
      throw new Error(`Invalid entity type number: ${number}`)
  }
}

export const EntityTypeMap = {
  AGENT: 1,
  GROUP: 10,
  ORGANIZATION: 90,
} as const satisfies Record<EntityType, number>

export const getEntityIdByEntityType = (
  entityType?: EntityType,
): number | undefined => {
  if (!entityType) return undefined

  return EntityTypeMap[entityType]
}

export const MessageTypeSchema = z.enum([
  'UPDATE_SOFTWARE',
  'SIGN_OUT',
  'CONFIRMATION',
  'READ_ONLY',
])

export const MessageType = t.Enum(MessageTypeSchema.enum)

export type MessageType = (typeof MessageType)['static']

export const MessageSeveritySchema = z.enum(['INFO', 'WARNING', 'CRITICAL'])

export const MessageSeverity = t.Enum(MessageSeveritySchema.enum)

export type MessageSeverity = (typeof MessageSeverity)['static']

export const MessageStatusSchema = z.enum([
  'DELAYED',
  'WAITING',
  'ACTIVE',
  'COMPLETED',
  'FAILED',
  'UNKNOWN',
])

export const MessageStatus = t.Enum(MessageStatusSchema.enum)

export type MessageStatus = (typeof MessageStatus)['static']

export const isMessageDeletable = (status: MessageStatus) => {
  const result = MessageStatusSchema.extract([
    'DELAYED',
    'WAITING',
    'UNKNOWN',
    'FAILED',
  ]).safeParse(status)

  return result.success
}

export const canMessageBeRetried = (status: MessageStatus) => {
  const result = MessageStatusSchema.extract(['FAILED']).safeParse(status)

  return result.success
}

export const Message = t.Object({
  id: t.String(),
  name: t.String(),
  message: t.String(),
  severity: MessageSeverity,
  message_type: MessageType,
  status: MessageStatus,
  created_at: t.String(),
  will_run_at: t.Optional(t.String()),
  processed_at: t.Optional(t.String()),
  finished_at: t.Optional(t.String()),
  created_by: t.Number(),
  entity_type_id: EntityType,
  is_deletable: t.Boolean(),
  can_be_retried: t.Boolean(),
  scheduler_id: t.Optional(t.String()),
  failed_reason: t.Optional(t.String()),
})

export type Message = (typeof Message)['static']

const ConstraintSchema = t.Object({
  id: t.Number(),
  name: t.String(),
  amount: t.MaybeEmpty(t.Number()),
})

export const ConstraintCategorySchema = t.Object({
  id: t.Number(),
  name: t.String(),
  constraints: t.Array(ConstraintSchema),
})

export type ConstraintCategory = (typeof ConstraintCategorySchema)['static']

export const LimitByNumbers = t.Object({
  id: t.MaybeEmpty(t.Number()),
  draw_id: t.Number(),
  draw_name: t.String(),
  game_id: t.Number(),
  game_name: t.String(),
  ballots: t.String(),
  entity_type: EntityType,
  entity_id: t.MaybeEmpty(t.Number()),
  entity_name: t.MaybeEmpty(t.String()),
  limit_amount: t.Number(),
  is_permanent: t.Boolean(),
})

export const Limit = t.Object({
  id: t.Number(),
  draw_id: t.Number(),
  draw_name: t.String(),
  game_id: t.Number(),
  game_name: t.String(),
  limit: t.Number(),
  entity_type: EntityType,
  entity_id: t.MaybeEmpty(t.Number()),
  entity_name: t.MaybeEmpty(t.String()),
})

export type Limit = (typeof Limit)['static']

export type LimitByNumbers = (typeof LimitByNumbers)['static']

export const FilterId = t.Partial(
  t.Object({
    eq: t.Number(),
    ne: t.Number(),
    in: t.Array(t.Number()),
  }),
)
export type FilterId = (typeof FilterId)['static']

export const FilterNumber = t.Partial(
  t.Object({
    eq: t.Number(),
    ne: t.Number(),
    gt: t.Number(),
    gte: t.Number(),
    lt: t.Number(),
    lte: t.Number(),
    in: t.Array(t.Number()),
  }),
)

export type FilterNumber = (typeof FilterNumber)['static']

export const FilterEntityType = t.Partial(
  t.Object({
    eq: EntityType,
    ne: EntityType,
    in: t.Array(EntityType),
  }),
)

export type FilterEntityType = (typeof FilterEntityType)['static']

export const PaginatedResponse = <T extends TSchema>(itemSchema: T) => {
  return t.Object({
    total: t.Number({ description: 'Total number of items available' }),
    items: t.Array(itemSchema, { description: 'Items for the current page' }),
    page: t.Number({ description: 'Current page number (1-based)' }),
    limit: t.Number({ description: 'Number of items per page' }),
  })
}
